import { jsPDF } from "jspdf";
import fempulseLogo from "../assets/images/fempulseLogo.png";

const BLACK = [51, 51, 51];
const GRAY_HEADER = [74, 144, 217];
const HEADER_TEXT = [255, 255, 255];
const GRAY_TEXT = [100, 100, 100];
const GREEN = [77, 182, 172];
const RED = [211, 47, 47];
const AMBER = [237, 194, 75];
const BLUE = [0, 169, 224];
const BORDER = [200, 220, 230];
const HEAD_H = 14;
const DATA_H = 16;
const LINE_H = 4;
const FS = 9;
const FS_HEAD = 9;

const lighten = (rgb, factor = 0.85) => rgb.map(c => Math.round(c + (255 - c) * factor));

const fmtDateLong = (str) =>
  str
    ? new Date(str + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : "—";

const fmtDateTime = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtUSDateTime = (str) => {
  if (!str || str === "No Activity" || str === "Error") return null;

  let d = new Date(str);

  // If parsing failed, try manual parse for DD/MM/YYYY format
  if (isNaN(d.getTime())) {
    const parts = String(str).split(',');
    if (parts.length >= 1) {
      const datePart = parts[0].trim();
      const dateMatch = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dateMatch) {
        const day = dateMatch[1];
        const month = dateMatch[2];
        const year = dateMatch[3];
        // Try parsing assuming DD/MM/YYYY
        d = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T12:00:00`);
      }
    }
  }

  if (isNaN(d.getTime())) return str;

  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const fmtDateShort = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

function wrapText(doc, text, maxW) {
  if (!text) return ["—"];
  const words = String(text).split(" ");
  const lines = [];
  let cur = "";
  for (const word of words) {
    let w = word;
    while (doc.getTextWidth(w) > maxW) {
      let chunk = "";
      for (const ch of w) {
        if (doc.getTextWidth(chunk + ch) > maxW) break;
        chunk += ch;
      }
      if (!chunk) chunk = w[0];
      const test = cur ? cur + " " + chunk : chunk;
      if (cur && doc.getTextWidth(test) > maxW) {
        lines.push(cur);
        cur = chunk;
      } else cur = test;
      w = w.slice(chunk.length);
    }
    if (w) {
      const test = cur ? cur + " " + w : w;
      if (cur && doc.getTextWidth(test) > maxW) {
        lines.push(cur);
        cur = w;
      } else cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : ["—"];
}

function drawRoundedRect(doc, x, y, w, h, r, style = "F") {
  doc.roundedRect(x, y, w, h, r, r, style);
}

function drawBadge(doc, x, y, text, bgColor, textColor, align = "left") {
  const paddingH = 3;
  const paddingV = 1.5;
  doc.setFontSize(8);
  const tw = doc.getTextWidth(text);
  const w = tw + paddingH * 2;
  const h = 5;
  if (align === "right") {
    x = x - w;
  }
  doc.setFillColor(...bgColor);
  drawRoundedRect(doc, x, y, w, h, 2, "F");
  doc.setTextColor(...textColor);
  doc.text(text, x + paddingH, y + 3.4);
  return w;
}

function drawProgressBar(doc, x, y, w, current, total) {
  const h = 2;
  const r = 1;
  const progress = Math.min(1, Math.max(0, current / total));
  doc.setFillColor(235, 235, 235);
  drawRoundedRect(doc, x, y, w, h, r, "F");
  doc.setFillColor(...BLUE);
  drawRoundedRect(doc, x, y, w * progress, h, r, "F");
}

function drawMetricBox(doc, x, y, w, label, value, sub = "") {
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_TEXT);
  doc.text(label.toUpperCase(), x, y);
  doc.setFontSize(14);
  doc.setTextColor(...BLACK);
  const valStr = String(value);
  doc.text(valStr, x, y + 6);
  if (sub) {
    const valW = doc.getTextWidth(valStr);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BLACK);
    doc.text(sub, x + valW + 2, y + 6);
    doc.setFont("helvetica", "normal");
  }
}

function drawCell(doc, x, y, w, h, lines, opts = {}) {
  const { bg, bold, color, fontSize = FS, align = "left" } = opts;
  if (bg) {
    doc.setFillColor(...bg);
    doc.rect(x, y, w, h, "F");
  }
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.rect(x, y, w, h);
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...(color || BLACK));

  lines.forEach((line, li) => {
    if (5 + li * LINE_H < h - 2) {
      let textX = x + 2;
      if (align === "center") {
        const tw = doc.getTextWidth(line);
        textX = x + (w - tw) / 2;
      }
      doc.text(String(line), textX, y + 7 + li * LINE_H);
    }
  });
}

const COLS_SUMMARY = [
  { label: "Subject ID", w: 30 },
  { label: "Latest\nActivity", w: 25 },
  { label: "Diary\nStatus", w: 20 },
  { label: "Voids", w: 14 },
  { label: "Leaks", w: 14 },
  { label: "Intake", w: 14 },
  { label: "Intake", w: 18 },
  { label: "Activity", w: 18 },
  { label: "Days\nExc/Sel", w: 20 },
  { label: "Status", w: 18 },
];

const COLS_DAILY = [
  { label: "Date", w: 22, align: "left" },
  { label: "Voids", w: 17, align: "left" },
  { label: "Leaks", w: 14, align: "left" },
  { label: "Fluids\n(oz)", w: 17, align: "left" },
  { label: "Urgency", w: 15, align: "left" },
  { label: "Intake", w: 17, align: "left" },
  { label: "Activity", w: 17, align: "left" },
  { label: "Status", w: 18, align: "left" },
  { label: "Reason", w: 55, align: "left" },
  { label: "Last Modified", w: 62, align: "left" },
];

const parseBasedOnSystem = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const match = raw.match(
    /(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{1,2}):(\d{2})\s*(am|pm)/i
  );
  if (!match) return null;

  let [, p1, p2, year, hours, minutes, meridiem] = match;

  // Assume DD/MM for system formatting since it's the most common globally in this app
  let day = p1;
  let month = p2;

  hours = parseInt(hours, 10);
  if (meridiem.toLowerCase() === "pm" && hours !== 12) hours += 12;
  if (meridiem.toLowerCase() === "am" && hours === 12) hours = 0;

  const d = new Date(
    `${year}-${month}-${day}T${String(hours).padStart(2, "0")}:${minutes}:00`
  );
  return isNaN(d.getTime()) ? null : d;
};

const getDiaryStatus = (raw) => {
  if (!raw || raw === "No Activity" || raw === "Error") {
    return { label: "Not Started", color: [140, 95, 30], bg: [250, 238, 218] };
  }
  const last = parseBasedOnSystem(raw);
  if (!last) {
    return { label: "Not Started", color: [140, 95, 30], bg: [250, 238, 218] };
  }
  const diffSeconds = (Date.now() - last.getTime()) / 1000;
  if (diffSeconds > 3 * 24 * 60 * 60) {
    return { label: "Not Started", color: [140, 95, 30], bg: [250, 238, 218] }; // Amber/Brown
  }
  return { label: "Started", color: GREEN };
};

export function generateAverageSummaryPDF({
  patientInfo = {},
  rows = [],
  activitiesData = {},
  startDate,
  endDate,
  globalDeselected = new Set(),
  globalAdded = new Set(),
  siteName = "",
  studyName = patientInfo.studyName || "EVANESCE II",
  onlyAverages = true,
  fileName = "",
}) {
  const activeRows = rows.filter(r => String(r.user?.status).toLowerCase() === "active");

  const toLS = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const dateRange = [];
  let cur = new Date(startDate + "T00:00:00");
  const endD = new Date(endDate + "T00:00:00");
  while (cur <= endD) {
    dateRange.push(toLS(cur));
    cur.setDate(cur.getDate() + 1);
  }
  globalAdded.forEach((d) => { if (!dateRange.includes(d)) dateRange.push(d); });
  const selectedDatesGlobal = dateRange.filter((d) => !globalDeselected.has(d));

  const getDisplayDates = () => {
    let minD = startDate;
    let maxD = endDate;
    if (globalAdded && globalAdded.size > 0) {
      globalAdded.forEach(d => {
        if (d < minD) minD = d;
        if (d > maxD) maxD = d;
      });
    }
    return { start: minD, end: maxD };
  };

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const mg = 14;
  let curY = mg;

  const ensureSpace = (n) => {
    if (curY + n > pageH - mg) {
      doc.addPage();
      curY = mg;
    }
  };


  const headerH = 45;
  const logoW = 70;
  const logoH = 19;
  try {
    doc.addImage(fempulseLogo, "PNG", pageW - mg - logoW, curY + 2, logoW, logoH);
  } catch (e) { }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BLUE);
  const studyTitleWithSite = siteName ? `${studyName} (${siteName})` : studyName;
  doc.text(studyTitleWithSite, mg, curY + 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...GRAY_TEXT);
  const reportTitle = onlyAverages ? "Average Summary" : "Detailed Summary";
  doc.text(reportTitle, mg, curY + 20);
  const genDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.text(`Generated On: ${genDate}.`, mg, curY + 28);
  const displayDates = getDisplayDates();
  const d1 = new Date(displayDates.start + "T00:00:00");
  const d2 = new Date(displayDates.end + "T00:00:00");
  const diffDays = Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
  doc.text(`Date Range: ${fmtDateLong(displayDates.start)} – ${fmtDateLong(displayDates.end)} (${diffDays} days)`, mg, curY + 35);


  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(...RED);
  doc.text(`* Summary data till  ${yesterdayStr}`, mg, curY + 42);
  // Reset font back to normal after the note
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...BLACK);

  // Divider line
  curY += headerH;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.5);
  doc.line(mg, curY, pageW - mg, curY);
  curY += 8;


  const statsW = (pageW - mg * 2 - 5) / 2;
  const statsH = 20;

  let totalComp = 0;
  activeRows.forEach(r => {
    const act = activitiesData[r.user?.id] || {};
    const count = (act.rawSummaries || []).filter(s => s.dayInclude !== "no").length;
    totalComp += Math.min(100, (count / 7) * 100);
  });
  const avgComp = activeRows.length > 0 ? (totalComp / activeRows.length).toFixed(0) : 0;

  const drawStatCard = (x, y, label, val, color) => {
    doc.setFillColor(248, 250, 255);
    drawRoundedRect(doc, x, y, statsW, statsH, 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(label.toUpperCase(), x + 4, y + 6);
    doc.setFontSize(16);
    doc.setTextColor(...color);
    doc.text(String(val), x + 4, y + 14);
  };

  drawStatCard(mg, curY, "Total Subjects", activeRows.length, BLACK);
  drawStatCard(mg + statsW + 5, curY, "Diary Completion", `${avgComp}%`, avgComp < 50 ? RED : GREEN);

  curY += statsH + 12;

  doc.setFontSize(10);
  doc.setTextColor(...GRAY_TEXT);
  doc.text("SUBJECTS", mg, curY);
  curY += 5;

  activeRows.forEach((r) => {
    const uid = r.user?.id;
    const site = r.user?.clinic_name || r.site || "Unknown Site";
    const act = activitiesData[uid] || {};
    const rawSummaries = act.rawSummaries || [];

    // Calculate Averages and Selected Dates for this user
    let voidsSum = 0, leaksSum = 0, intakeSum = 0, nightSum = 0;
    let intakeCountSum = 0, totalActSum = 0, urineToiletSum = 0, count = 0;

    let includedCount = 0;
    const userTableRows = dateRange.map(dateStr => {
      const rec = rawSummaries.find(s => s.date === dateStr);
      const isGloballyDeselected = globalDeselected.has(dateStr);
      const isLocallyExcluded = rec && rec.dayInclude === "no";

      const isIncluded = !isGloballyDeselected && !isLocallyExcluded;
      if (isIncluded) {
        voidsSum += rec?.voidActivityCount || 0;
        leaksSum += rec?.totalLeaks || 0;
        intakeSum += rec?.intakeMl || 0;
        nightSum += rec?.nightVoids || 0;
        intakeCountSum += rec?.intakeActivityCount || 0;
        totalActSum += rec?.totalActivityCount || 0;
        urineToiletSum += rec?.urineInToilet || 0;
        includedCount++;
      }
      return { date: dateStr, record: rec || null, isIncluded };
    });

    const totalExcluded = dateRange.length - includedCount;
    const countText = `${totalExcluded} / ${includedCount}`;

    const avgVoids = includedCount > 0 ? (voidsSum / includedCount).toFixed(1) : "0.0";
    const avgLeaks = includedCount > 0 ? (leaksSum / includedCount).toFixed(1) : "0.0";
    const avgIntake = includedCount > 0 ? (intakeSum / includedCount).toFixed(0) : "0";
    const avgNight = includedCount > 0 ? (nightSum / includedCount).toFixed(1) : "0.0";
    const avgIntakeCount = includedCount > 0 ? (intakeCountSum / includedCount).toFixed(1) : "0.0";
    const avgTotalAct = includedCount > 0 ? (totalActSum / includedCount).toFixed(1) : "0.0";
    const avgUrineToilet = includedCount > 0 ? (urineToiletSum / includedCount).toFixed(1) : "0.0";

    const userPrefix = r.user?.name || (r.user?.email || uid || "").split("@")[0] || "—";
    const diaryStatus = getDiaryStatus(act.latestActivity);

    const cardH = 55;
    ensureSpace(cardH + 10);


    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.setFillColor(255, 255, 255);
    drawRoundedRect(doc, mg, curY, pageW - mg * 2, cardH, 2, "FD");


    const avatarSize = 10;
    doc.setFillColor(...GRAY_HEADER);
    drawRoundedRect(doc, mg + 5, curY + 5, avatarSize, avatarSize, avatarSize / 2, "F");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    const initials = userPrefix.slice(0, 2).toUpperCase();
    doc.text(initials, mg + 5 + (avatarSize - doc.getTextWidth(initials)) / 2, curY + 5 + 6.5);

    doc.setFontSize(11);
    doc.setTextColor(...BLACK);
    doc.text(`Patient ${userPrefix}`, mg + 5 + avatarSize + 4, curY + 9);
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(r.user?.email || "—", mg + 5 + avatarSize + 4, curY + 13);


    let badgeX = pageW - mg - 10;

    const st = String(r.user?.status || "").toLowerCase();
    if (st) {
      const stColor = st === "active" ? GREEN : BLUE;
      const stW = drawBadge(doc, badgeX, curY + 6, st.toUpperCase(), lighten(stColor), stColor, "right");
      badgeX -= (stW + 5);
    }

    if (diaryStatus && diaryStatus.label) {
      const dsW = drawBadge(doc, badgeX, curY + 6, diaryStatus.label.toUpperCase(), diaryStatus.bg || lighten(diaryStatus.color), diaryStatus.color, "right");
      badgeX -= (dsW + 5);
    }

    const metricsY = curY + 22;
    const colW = (pageW - mg * 2 - 20) / 3;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.1);
    doc.line(mg + 5, metricsY - 4, pageW - mg - 5, metricsY - 4);

    drawMetricBox(doc, mg + 10, metricsY + 4, colW, "Voids", avgVoids);
    drawMetricBox(doc, mg + 10 + colW, metricsY + 4, colW, "Leaks", avgLeaks);
    drawMetricBox(doc, mg + 10 + colW * 2, metricsY + 4, colW, "Fluids", avgIntake, "oz");


    const row2Y = metricsY + 12;
    drawMetricBox(doc, mg + 10, row2Y + 4, colW, "Intake", avgIntakeCount);
    drawMetricBox(doc, mg + 10 + colW, row2Y + 4, colW, "Activity", avgTotalAct);
    drawMetricBox(doc, mg + 10 + colW * 2, row2Y + 4, colW, "Days", countText);

    const cardFooterY = curY + cardH - 8;
    doc.line(mg + 5, cardFooterY, pageW - mg - 5, cardFooterY);

    doc.setFontSize(8);
    doc.setTextColor(...GRAY_TEXT);

    const lastSeenFormatted = fmtUSDateTime(act.latestActivity);
    const lastSeen = lastSeenFormatted ? `Last seen ${lastSeenFormatted}` : "No Activity found";
    doc.text(lastSeen, pageW - mg - 10 - doc.getTextWidth(lastSeen), cardFooterY + 5);

    curY += cardH + 6;

    if (!onlyAverages) {
      ensureSpace(60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...BLACK);
      doc.text("Daily Details:", mg, curY);
      curY += 4;

      let x = mg;
      COLS_DAILY.forEach(col => {
        drawCell(doc, x, curY, col.w, HEAD_H, col.label.split("\n"), { bg: GRAY_HEADER, bold: true, fontSize: FS_HEAD - 1, align: col.align || "left", color: HEADER_TEXT });
        x += col.w;
      });
      curY += HEAD_H;

      userTableRows.forEach((row, ri) => {
        const rec = row.record;
        const isIncluded = row.isIncluded;
        const statusLabel = isIncluded ? "Included" : "Excluded";
        const statusColor = isIncluded ? GREEN : RED;
        const reason = rec?.dayexcludeReason ? `${rec.dayexcludeReason} (By: ${rec.dayIncludeUserName || 'Admin'})` : "–";
        const lastMod = rec?.lastModified ? fmtDateTime(rec.lastModified) : "–";

        const dailyVals = [
          [fmtDateShort(row.date)],
          [String(rec?.voidActivityCount ?? 0)],
          [String(rec?.totalLeaks ?? 0)],
          [String(rec?.intakeMl ?? 0)],
          [String(rec?.strongUrge ?? 0)],
          [String(rec?.intakeActivityCount ?? 0)],
          [String(rec?.totalActivityCount ?? 0)],
          [statusLabel],
          wrapText(doc, reason, COLS_DAILY[8].w - 4),
          wrapText(doc, lastMod, COLS_DAILY[9].w - 4),
        ];

        x = mg;
        ensureSpace(10);
        const rowBg = ri % 2 === 1 ? [245, 245, 245] : null;
        COLS_DAILY.forEach((col, ci) => {
          let color = BLACK;
          if (ci === 7) color = statusColor;
          const isBold = (ci === 0 || ci === 8);

          drawCell(doc, x, curY, col.w, 10, dailyVals[ci], {
            fontSize: FS - 2,
            color,
            align: "left",
            bg: rowBg,
            bold: isBold
          });
          x += col.w;
        });
        curY += 10;
      });
      curY += 8;
    }

    curY += 15;
  });

  const totalPg = doc.getNumberOfPages();
  for (let p = 1; p <= totalPg; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(
      `FemPulse Research Platform `,
      mg,
      pageH - 5
    );
    doc.text(`Page ${p} of ${totalPg}`, pageW - mg, pageH - 5, { align: "right" });
  }

  const year = new Date().getFullYear();
  const today = new Date().toISOString().split("T")[0];
  const pdfType = onlyAverages ? `Average-Report` : `Detailed-Summary`;
  const defaultName = `${today}-FP-${pdfType}-${year}-${(siteName || "AllSites").replace(/[\s_]+/g, "-")}`;
  
  let finalName = (fileName || defaultName).replace(/\.pdf$/i, "");
  // Replace spaces and underscores with hyphens
  finalName = finalName.replace(/[\s_]+/g, "-");
  
  // Ensure the year is present in the final name
  if (!finalName.includes(String(year))) {
    finalName = `${finalName}-${year}`;
  }

  doc.save(`${finalName}.pdf`);
}
